import type { FC, ReactNode } from 'react';
import { createContext, useState, useMemo, useContext } from 'react';

interface CaseStudiesLayerContextProps {
  showCaseStudiesLayer: boolean;
  toggleCaseStudiesLayer: () => void;
  setCaseStudiesLayerVisible: (visible: boolean) => void;
}

const CaseStudiesLayerContext = createContext<
  CaseStudiesLayerContextProps | undefined
>(undefined);

interface Props {
  children: ReactNode;
}

export const useCaseStudiesLayer = () => {
  const context = useContext(CaseStudiesLayerContext);

  if (!context) {
    throw new Error(
      'useCaseStudiesLayer hook must be used within a CaseStudiesLayerProvider',
    );
  }

  return context;
};

/**
 * For managing case studies layer visibility state
 */
export const CaseStudiesLayerProvider: FC<Props> = ({ children }) => {
  const [showCaseStudiesLayer, setShowCaseStudiesLayer] = useState(false);

  const toggleCaseStudiesLayer = () => {
    setShowCaseStudiesLayer(prev => !prev);
  };

  const setCaseStudiesLayerVisible = (visible: boolean) => {
    setShowCaseStudiesLayer(visible);
  };

  const contextValue = useMemo(
    () => ({
      showCaseStudiesLayer,
      toggleCaseStudiesLayer,
      setCaseStudiesLayerVisible,
    }),
    [showCaseStudiesLayer],
  );

  return (
    <CaseStudiesLayerContext.Provider value={contextValue}>
      {children}
    </CaseStudiesLayerContext.Provider>
  );
};
